'use client'

import { useTranslations } from 'next-intl'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from '@/components/ui/pagination'

type PropsType = {
  currentPage: number
  totalPages: number
  totalItems: number
  pageSize: number
  onPageChangeAction: (page: number) => void
  onPageSizeChangeAction: (size: number) => void
}

export function DataTablePagination({
  currentPage,
  totalPages,
  totalItems: _totalItems,
  pageSize,
  onPageChangeAction,
  onPageSizeChangeAction
}: PropsType) {
  const t = useTranslations()

  const getVisiblePages = () => {
    const delta = 1
    const range = []
    const rangeWithDots = []

    if (totalPages <= 1) {
      return [1]
    }

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i)
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...')
    } else {
      rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages)
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages)
    }

    return rangeWithDots
  }

  return (
    <div className="flex flex-col items-center space-y-4 sm:flex-row sm:justify-between sm:space-y-0">
      <div className="flex items-center space-x-2">
        <p className="text-sm font-medium">{t('rows_per_page')}</p>
        <Select value={pageSize.toString()} onValueChange={value => onPageSizeChangeAction(Number(value))}>
          <SelectTrigger className="h-8 w-17.5">
            <SelectValue placeholder={pageSize.toString()} />
          </SelectTrigger>
          <SelectContent side="top">
            {[10, 20, 30, 40, 50].map(size => (
              <SelectItem key={size} value={size.toString()}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col items-center space-y-2 sm:flex-row sm:space-y-0 sm:space-x-6">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => onPageChangeAction(currentPage - 1)}
                className={currentPage <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>

            {getVisiblePages().map((page, index) => (
              <PaginationItem key={index}>
                {page === '...' ? (
                  <PaginationEllipsis />
                ) : (
                  <PaginationLink
                    onClick={() => onPageChangeAction(page as number)}
                    isActive={currentPage === page}
                    className="cursor-pointer">
                    {page}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}

            <PaginationItem>
              <PaginationNext
                onClick={() => onPageChangeAction(currentPage + 1)}
                className={currentPage >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  )
}
